import React, { useState, useMemo, useContext, useEffect } from 'react';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft, SubHeaderRight } from '../../../../layout/SubHeader/SubHeader';
import Page from '../../../../layout/Page/Page';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import Dropdown, { DropdownToggle, DropdownMenu, DropdownItem } from '../../../../components/bootstrap/Dropdown';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import Chart from '../../../../components/extras/Chart';
import useDarkMode from '../../../../hooks/useDarkMode';
import { COLORS_DARK, COLORS_LIGHT } from '../../../../types/dashColors';
import Icon from '../../../../components/icon/Icon';
import Spinner from '../../../../components/bootstrap/Spinner';
import PaymentDateModal from './PaymentDateModal';
import Popovers from '../../../../components/bootstrap/Popovers';
import { DefaultContext } from '../../../../contexts/default';
import BillingChargesDB from '../../../../database/wrappers/billingCharges';
import BillingScheduleDB from '../../../../database/wrappers/billingSchedule';
import StoreDB from '../../../../database/wrappers/store';
import { where } from 'firebase/firestore';
import Money from '../../../../helpers/utils/money/money';
import showNotification from '../../../../components/extras/showNotification';

const months = [
  'Jan', 'Fev', 'Mar', 'Abr', 'mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const EstablishmentSubscription = () => {
  const { darkModeStatus } = useDarkMode();
  const themeColors = darkModeStatus ? COLORS_DARK : COLORS_LIGHT;
  const { estabSelected } = useContext(DefaultContext);

  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(months[currentMonthIndex]);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedEstablishmentForModal, setSelectedEstablishmentForModal] = useState(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [totals, setTotals] = useState({
    paid: { value: 0, count: 0 },
    pending: { value: 0, count: 0 },
    overdue: { value: 0, count: 0 },
    calls: { value: 0, count: 0 }
  });

  const monthIndex = useMemo(() => months.indexOf(selectedMonth), [selectedMonth]);

  useEffect(() => {
    if (!estabSelected) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const storeDB = new StoreDB(estabSelected);
        const stores = await storeDB.getAll();
        const storeMap = stores.reduce((acc, store) => {
          acc[store.id] = store.name;
          return acc;
        }, {});

        const billingDB = new BillingChargesDB();
        let charges = await billingDB.getAll(
          where('estabId', '==', estabSelected)
        );

        const scheduleDB = new BillingScheduleDB();
        const schedules = await scheduleDB.getAll(
          where('estabId', '==', estabSelected)
        );

        const scheduleMap = schedules.reduce((acc, schedule) => {
          acc[schedule.storeId] = schedule;
          return acc;
        }, {});

        const yearCharges = charges.filter(charge => {
          if (!charge.created_at) return false;
          return charge.created_at.toDate().getFullYear() === selectedYear;
        });

        const currentMonthCharges = yearCharges.filter(charge => {
          const date = charge.created_at.toDate();
          return date.getMonth() === monthIndex;
        });

        const newRows = currentMonthCharges.map(charge => {
          const status = mapStatus(charge.status, charge.created_at.toDate(), charge.report);
          const schedule = scheduleMap[charge.storeId];

          let currentChargeDate = '--';
          let nextChargeDate = '-';

          const getFormattedDate = (dayValue, baseDateObj, isNext = false) => {
            if (!dayValue) return isNext ? '-' : '--';

            let day;
            if (dayValue && typeof dayValue === 'object' && typeof dayValue.toDate === 'function') {
              day = dayValue.toDate().getDate();
            } else {
              day = parseInt(dayValue);
            }

            if (!day || isNaN(day)) return isNext ? '-' : '--';

            const d = new Date(baseDateObj);
            if (isNext) {
              d.setMonth(d.getMonth() + 1);
            }

            const year = d.getFullYear();
            const month = d.getMonth();
            const lastDay = new Date(year, month + 1, 0).getDate();
            const finalDay = Math.min(day, lastDay);

            return new Date(year, month, finalDay).toLocaleDateString('pt-BR');
          };

          if (charge.dueDate) {
            currentChargeDate = charge.dueDate.toDate().toLocaleDateString('pt-BR');
          } else if (schedule) {
            currentChargeDate = getFormattedDate(schedule.currentChargeDate, charge.created_at.toDate());
            nextChargeDate = getFormattedDate(schedule.nextChargeDate, charge.created_at.toDate(), true);
          }

          return {
            id: charge.id,
            data: `${selectedMonth}. ${selectedYear}`,
            loja: storeMap[charge.storeId] || 'Loja não encontrada',
            vencimento: currentChargeDate,
            vencimentoProximo: nextChargeDate,
            pagamento: charge.status === 'PAID' ? (charge.updated_at ? charge.updated_at.toDate().toLocaleDateString('pt-BR') : '--') : '--',
            assinatura: 1,
            pos: charge.items.pos?.qtdInitial || 0,
            posDelivery: charge.items.posDelivery?.qtdInitial > 0 ? 'Ativo' : 'Inativo',
            totem: charge.items.totem?.qtdInitial || 0,
            fiscal: charge.fiscalQtd,
            locacao: Money.centsToMaskMoney(charge.items.totem?.totalFinal || 0),
            desconto: Money.centsToMaskMoney(charge.totalDiscount),
            totalLiquido: Money.centsToMaskMoney(charge.totalInitial),
            imposto: Money.centsToMaskMoney(0),
            total: Money.centsToMaskMoney(charge.totalToPay),
            status: status,
            original: charge
          };
        });

        setRows(newRows);

        const newTotals = {
          paid: { value: 0, count: 0 },
          pending: { value: 0, count: 0 },
          overdue: { value: 0, count: 0 },
          calls: { value: 0, count: 0 }
        };

        const getDueDate = (charge, schedule) => {
          if (charge.dueDate) {
            return charge.dueDate.toDate();
          }

          const createdDate = charge.created_at.toDate();
          if (!schedule || !schedule.currentChargeDate) {
            return createdDate;
          }

          let day;
          const dayValue = schedule.currentChargeDate;

          if (dayValue && typeof dayValue === 'object' && typeof dayValue.toDate === 'function') {
            day = dayValue.toDate().getDate();
          } else {
            day = parseInt(dayValue);
          }

          if (!day || isNaN(day)) return createdDate;

          const year = createdDate.getFullYear();
          const month = createdDate.getMonth();
          const lastDay = new Date(year, month + 1, 0).getDate();
          const finalDay = Math.min(day, lastDay);

          return new Date(year, month, finalDay);
        };

        currentMonthCharges.forEach(charge => {
          const val = charge.totalToPay;
          const status = charge.status;
          const report = charge.report;
          const today = new Date();
          const schedule = scheduleMap[charge.storeId];
          const dueDate = getDueDate(charge, schedule);

          const todayClean = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const dueDateClean = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());

          if (report && report.status === 'OPEN') {
            newTotals.calls.value += val;
            newTotals.calls.count++;
          }

          if (status === 'PAID') {
            newTotals.paid.value += val;
            newTotals.paid.count++;
          } else if (status === 'PENDING' || status === 'GENERATED') {
            if (dueDateClean < todayClean) {
              newTotals.overdue.value += val;
              newTotals.overdue.count++;
            } else {
              newTotals.pending.value += val;
              newTotals.pending.count++;
            }
          }
        });

        setTotals(newTotals);

        const storeSeriesData = {};

        yearCharges.forEach(charge => {
          const storeName = storeMap[charge.storeId] || 'Desconhecida';
          if (!storeSeriesData[storeName]) {
            storeSeriesData[storeName] = new Array(12).fill(0);
          }
          const month = charge.created_at.toDate().getMonth();
          storeSeriesData[storeName][month] += (charge.totalToPay / 100);
        });

        const newSeries = Object.keys(storeSeriesData).map(name => ({
          name,
          data: storeSeriesData[name]
        }));

        setChartSeries(newSeries);

      } catch (error) {
        showNotification('Erro', 'Erro ao buscar dados', 'danger');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [estabSelected, selectedYear, selectedMonth, monthIndex]);

  const mapStatus = (status) => {
    if (status === 'PENDING') return 'Pendente';
    if (status === 'GENERATED') return 'Pendente';
    if (status === 'PAID') return 'Pago';
    if (status === 'CANCELED') return 'Vencida';
    return status;
  };

  const columns = useMemo(() => [
    { label: 'DATA', field: 'data' },
    { label: 'LOJA', field: 'loja' },
    { label: 'VENCIMENTO ATUAL', field: 'vencimento' },
    { label: 'VENCIMENTO P/MÊS', field: 'vencimentoProximo' },
    { label: 'PAGAMENTO', field: 'pagamento' },
    { label: 'ASSINATURA', field: 'assinatura' },
    { label: 'POS', field: 'pos' },
    {
      label: 'POS DELIVERY',
      field: 'posDelivery',
      format: row => (
        <Button
          isLight
          color={row.posDelivery === 'Ativo' ? 'success' : 'danger'}
          icon='Circle'
          className='text-nowrap'
          size="sm"
        >
          {row.posDelivery}
        </Button>
      )
    },
    { label: 'TOTEM', field: 'totem' },
    { label: 'FISCAL', field: 'fiscal' },
    { label: 'LOCAÇÃO', field: 'locacao' },
    { label: 'DESCONTO', field: 'desconto' },
    { label: 'TOTAL LIQUIDO', field: 'totalLiquido' },
    { label: 'TOTAL', field: 'total' },
    {
      label: 'STATUS',
      field: 'status',
      format: row => {
        let color = 'warning';
        let icon = 'AccessTime';

        if (row.status === 'Pago') {
          color = 'success';
          icon = 'Savings';
        } else if (row.status === 'Vencida') {
          color = 'danger';
          icon = 'Warning';
        }

        return (
          <Button
            isLight
            color={color}
            icon={icon}
            className='text-nowrap'
            size="sm"
          >
            {row.status}
          </Button>
        )
      }
    },
    {
      label: 'AÇÃO',
      field: 'actions',
      format: row => (
        <Popovers trigger='hover' desc={'Editar'}>
          <Button
            color="dark"
            icon="Edit"
            size="sm"
            shadow="sm"
            onClick={() => {
              setSelectedEstablishmentForModal({
                storeId: row.original.storeId,
                estabId: row.original.estabId
              });
              setIsPaymentModalOpen(true);
            }}
          />
        </Popovers>
      ),
    }
  ], []);

  const chartOptions = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: { show: false },
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    colors: ['#8b5cf6', '#fbbf24', '#22d3ee'],
    xaxis: {
      categories: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
      title: { text: 'Tempo', style: { fontSize: '14px', fontWeight: 'bold', color: themeColors.FONT } },
      labels: { style: { colors: themeColors.FONT } }
    },
    yaxis: {
      title: { text: 'Valor', style: { fontSize: '14px', fontWeight: 'bold', color: themeColors.FONT } },
      labels: {
        style: { colors: themeColors.FONT },
        formatter: (val) => Money.maskMoney(val),
      },
    },
    tooltip: {
      y: {
        formatter: (val) => Money.maskMoney(val),
      },
    },
    legend: {
      position: 'bottom',
      labels: { colors: themeColors.FONT }
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    }
  };

  const TotalCard = ({ title, value, subValue, color }) => (
    <div className={`col-md-3`}>
      <div className={`p-3 rounded-3`} style={{ backgroundColor: color }}>
        <div className="fw-bold mb-1" style={{ fontSize: '0.9rem', color: '#333' }}>{title}</div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bolder" style={{ fontSize: '1.2rem', color: '#000' }}>{value}</span>
          {subValue && <span className="fw-bold" style={{ fontSize: '1.2rem', color: '#000' }}>- {subValue}</span>}
        </div>
      </div>
    </div>
  );

  return (
    <PageWrapper title="Assinatura por estabelecimento">
      <SubHeader>
        <SubHeaderLeft>
          <Breadcrumb
            list={[
              { title: 'Assinatura', to: '/subscription/establishment' },
              { title: 'Estabelecimento', to: '/subscription/establishment' },
            ]}
          />
        </SubHeaderLeft>
        <SubHeaderRight>
          <div className="d-flex align-items-center gap-2">
            <Dropdown>
              <DropdownToggle>
                <div className="bg-white rounded-3 px-3 py-1 border d-flex align-items-center" style={{ height: '40px', cursor: 'pointer' }}>
                  <span className="fw-bold text-black">{selectedYear}</span>
                </div>
              </DropdownToggle>
              <DropdownMenu>
                {Array.from({ length: Math.max(0, currentYear - 2025 + 1) }, (_, i) => 2025 + i).map(year => (
                  <DropdownItem key={year} onClick={() => setSelectedYear(year)}>
                    {String(year)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
            <div className="d-flex">
              {months.map((month) => (
                <Button
                  key={month}
                  color={selectedMonth === month ? 'warning' : 'light'}
                  className={`rounded-3 mx-1 border-0 ${selectedMonth === month ? 'fw-bold' : 'text-muted'}`}
                  size="sm"
                  onClick={() => setSelectedMonth(month)}
                  style={{ minWidth: '40px' }}
                >
                  {month}
                </Button>
              ))}
            </div>
          </div>
        </SubHeaderRight>
      </SubHeader>

      <Page container='fluid'>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <Card className="mb-4">
              <CardHeader>
                <CardLabel>
                  <div className="d-flex align-items-center justify-content-center">
                    <Icon icon="Circle" iconColor="dark" />
                    <CardTitle className="ms-2">Totais</CardTitle>
                  </div>
                </CardLabel>
              </CardHeader>
              <CardBody>
                <div className="row g-3">
                  <TotalCard
                    title="Valor arrecadado - lojas pagadoras"
                    value={Money.centsToMaskMoney(totals.paid.value)}
                    subValue={totals.paid.count}
                    color="#d1fae5"
                  />
                  <TotalCard
                    title="Valor pendente - lojas pendentes"
                    value={Money.centsToMaskMoney(totals.pending.value)}
                    subValue={totals.pending.count}
                    color="#fee2e2"
                  />
                  <TotalCard
                    title="Valor vencido - lojas vencidas"
                    value={Money.centsToMaskMoney(totals.overdue.value)}
                    subValue={totals.overdue.count}
                    color="#fce7f3"
                  />
                  <TotalCard
                    title="Chamados de lojas"
                    value={totals.calls.count}
                    color="#f3e8ff"
                  />
                </div>
              </CardBody>
            </Card>

            <Card className="mb-5">
              <CardBody>
                <h4 className="mb-3">Assinatura por estabelecimento</h4>
                <div className="row d-flex align-items-end mb-0">
                  <SelectEstablishment class="col-md-3" />
                </div>
                <TableCustom
                  title=""
                  columns={columns}
                  rows={rows}
                  fileName={'AssinaturaEstabelecimento'}
                />
              </CardBody>
            </Card>

            <Card>
              <h3 className="mb-4 p-4 pb-0">Faturamento por loja</h3>
              <CardBody>
                <Chart
                  options={chartOptions}
                  series={chartSeries}
                  type="line"
                  height={350}
                />
              </CardBody>
            </Card>
          </>
        )}
      </Page>

      <PaymentDateModal
        isOpen={isPaymentModalOpen}
        setIsOpen={setIsPaymentModalOpen}
        selectedEstablishment={selectedEstablishmentForModal}
      />
    </PageWrapper>
  );
};

export default EstablishmentSubscription;
